import { selectPartner360Data } from 'legacy/lut/features/partner360/configuration'
import { FC, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  FormDropdown,
  FormInput,
  FormNumberInput,
  FormSwitch,
  FormTextarea,
  resetAllModifiedDataByKey,
  useGetFormData,
} from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'

import { useGetEspPartnerQuery } from '../../api/espApi'
import { createEspSponsorsCrud, ESP_ACTIVE_SPONSORS_FILTER } from '../../api/espSponsorsCrud'
import { ESP_TRANSLATION_KEYS as KEYS } from '../../configs/translations'
import { ESP_REQUEST_FORM_KEY } from '../../constants'
import { getEspAccountInfo } from '../../lib/getEspAccountInfo'
import { IEspSponsorOption, TEspRequestType } from '../../model/types'

import { getInitialEspRequestForm, IEspRequestFormData } from './getInitialEspRequestForm'

import styles from './EspRequestForm.module.scss'

const FORM_KEY = ESP_REQUEST_FORM_KEY
const SPONSOR_SEARCH_FIELDS = ['Name', 'Title']

interface IEspRequestFormProps {
  partnerId: string
  mode: TEspRequestType
}

/**
 * The sponsor assignment request form fields, shared by enrollment and sponsor change.
 * Section 1 is a read-only snapshot of the partner record; Section 2 is editable and, on
 * a change, pre-filled from the partner's most recent request. The justification is
 * required on a change and optional on an enrollment. The title and the action buttons live in the modal's fixed
 * header/footer (EspRequestFormModal / EspRequestFormFooter) — only these fields scroll.
 */
export const EspRequestForm: FC<IEspRequestFormProps> = ({ partnerId, mode }) => {
  const dispatch = useAppDispatch()
  const partnerData = useSelector(selectPartner360Data)
  const { data: espPartner } = useGetEspPartnerQuery({ partnerId })
  const partnerRequests = espPartner?.requests

  const accountInfo = useMemo(() => getEspAccountInfo(partnerData), [partnerData])

  const initialForm = useMemo(
    () => getInitialEspRequestForm(partnerRequests?.[0] ?? null),
    [partnerRequests]
  )

  useGetFormData<IEspRequestFormData>(FORM_KEY, initialForm)

  const sponsorsCrud = useMemo(createEspSponsorsCrud, [])

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(FORM_KEY))
    }
  }, [dispatch])

  return (
    <div className={styles.root}>
      <FormDropdown<IEspSponsorOption>
        isRequired
        formKey={FORM_KEY}
        field="sponsorName"
        defaultValue={initialForm.sponsorName}
        source={sponsorsCrud}
        caption="Name"
        fieldsToSearch={SPONSOR_SEARCH_FIELDS}
        filter={ESP_ACTIVE_SPONSORS_FILTER}
        label={KEYS.requestForm.sponsorLabel}
        renderFunction={(sponsor) => (
          <span>{[sponsor.Name, sponsor.Title].filter(Boolean).join(' · ')}</span>
        )}
      />

      <FormTextarea
        isRequired={mode === 'change'}
        formKey={FORM_KEY}
        field="justification"
        defaultValue={initialForm.justification}
        label={KEYS.requestForm.justification}
        rows={3}
      />

      <div className={styles.sectionTitle}>
        <Translation text={KEYS.requestForm.accountSectionTitle} span />
      </div>

      <div className={styles.accountGrid}>
        <div className={styles.accountField}>
          <span className={styles.accountLabel}>
            <Translation text={KEYS.requestForm.partnerName} span />
          </span>
          <span className={styles.accountValue}>{accountInfo.partnerName}</span>
        </div>
        <div className={styles.accountField}>
          <span className={styles.accountLabel}>
            <Translation text={KEYS.requestForm.partnerId} span />
          </span>
          <span className={styles.accountValue}>{accountInfo.partnerId}</span>
        </div>
        <div className={styles.accountField}>
          <span className={styles.accountLabel}>
            <Translation text={KEYS.requestForm.partnerCountry} span />
          </span>
          <span className={styles.accountValue}>{accountInfo.partnerCountry}</span>
        </div>
        <div className={styles.accountField}>
          <span className={styles.accountLabel}>
            <Translation text={KEYS.requestForm.accountClassification} span />
          </span>
          <span className={styles.accountValue}>{accountInfo.accountClassification}</span>
        </div>
        <div className={styles.accountField}>
          <span className={styles.accountLabel}>
            <Translation text={KEYS.requestForm.accountSegment} span />
          </span>
          <span className={styles.accountValue}>{accountInfo.accountSegment}</span>
        </div>
      </div>

      <div className={styles.sectionTitle}>
        <Translation text={KEYS.requestForm.additionalSectionTitle} span />
      </div>

      <div className={styles.two}>
        <FormNumberInput
          formKey={FORM_KEY}
          field="employeesCount"
          defaultValue={initialForm.employeesCount}
          label={KEYS.requestForm.employeesCount}
        />
        <FormNumberInput
          formKey={FORM_KEY}
          field="annualRevenueM"
          defaultValue={initialForm.annualRevenueM}
          label={KEYS.requestForm.annualRevenue}
        />
      </div>

      <div className={styles.two}>
        <FormInput
          formKey={FORM_KEY}
          field="accountValue"
          defaultValue={initialForm.accountValue}
          type="text"
          label={KEYS.requestForm.accountValue}
        />
        <FormInput
          formKey={FORM_KEY}
          field="accountContact"
          defaultValue={initialForm.accountContact}
          type="text"
          label={KEYS.requestForm.accountContact}
        />
      </div>

      <FormSwitch
        formKey={FORM_KEY}
        field="isBusinessGrowing"
        defaultValue={initialForm.isBusinessGrowing}
        label={KEYS.requestForm.isBusinessGrowing}
      />

      <FormTextarea
        formKey={FORM_KEY}
        field="missionAndVision"
        defaultValue={initialForm.missionAndVision}
        label={KEYS.requestForm.missionAndVision}
        rows={3}
      />

      <FormTextarea
        formKey={FORM_KEY}
        field="keyChallenges"
        defaultValue={initialForm.keyChallenges}
        label={KEYS.requestForm.keyChallenges}
        rows={3}
      />

      <FormTextarea
        formKey={FORM_KEY}
        field="productsAndSolutions"
        defaultValue={initialForm.productsAndSolutions}
        label={KEYS.requestForm.productsAndSolutions}
        rows={3}
      />
    </div>
  )
}

EspRequestForm.displayName = 'EspRequestForm'
